/* Dragging a booked service onto the board.
 *
 * Two places hand out the same card — the Due now row on the top bar, and the
 * appointments pane — so the behaviour lives here rather than in either of
 * them. Both start a drag through `start()`, both put down the same three kinds
 * of drop:
 *
 *   onto a square with an unlinked turn in it   attach the client to it,
 *                                               finished or still running
 *   onto a taken square, card already over      ask: one sitting, or its own
 *                                               square with the rest moved down
 *   onto the top strip of a square              put it there and move the rest
 *                                               of the column down
 *   onto the empty half of a half square        start the turn as the other
 *                                               half, completing the square
 *   onto an empty square in a column            start the turn there
 *
 * Which squares accept a drop is the store's answer, not this file's; see
 * bookedDropAt. This only works out what is under the pointer.
 *
 * A host only constrains the direction when something else wants the gesture.
 * The pane scrolls vertically, so a drag out of it has to be horizontal or the
 * list could never be scrolled again. The Due now row wraps rather than
 * scrolls, so its cards come off in any direction — which is the only honest
 * rule, because a card is dragged straight at the column it is meant for and
 * that line is diagonal far more often than not.
 */
import * as Vue from 'vue';
import { store } from '../store.js';

var THRESHOLD = 6;
var EDGE_BAND = 18;      // px of a square that means "put it above this"

var ghost = Vue.reactive({ show: false, x: 0, y: 0, text: '' });

var pending = null;
var dragging = false;
var suppress = false;

function clear() {
  window.removeEventListener('pointermove', onMove);
  window.removeEventListener('pointerup', onUp);
  window.removeEventListener('pointercancel', onUp);
  pending = null;
  dragging = false;
  ghost.show = false;
  store.ui.apptDrag = null;
}

// opts: { link, serviceId, staffIds, startsAt, durationMinutes, label, axis }
// `staffIds` is set when an option on the booking limits who may take it; only
// those columns light up and only they accept the drop.
// `axis` is 'x' or 'y' when the host has a scroll to protect; leave it out and
// the card comes off in whatever direction it is pulled.
// `serviceId` is the tracker service the booking maps to, or null when it is
// unmapped — an unmapped card can still be attached to a turn somebody has
// already started, it just cannot start one of its own. `startsAt` and
// `durationMinutes` are what tell the store whether this is work starting now
// or work that finished earlier and is only being written down.
function start(evt, opts) {
  if (evt.button != null && evt.button !== 0) return;
  if (store.turnForApptService(opts.link.appointmentServiceId)) return;   // on the board already
  pending = {
    link: opts.link, serviceId: opts.serviceId || null, label: opts.label,
    staffIds: opts.staffIds || null,
    startsAt: opts.startsAt || null, durationMinutes: opts.durationMinutes || 0,
    axis: opts.axis === 'x' || opts.axis === 'y' ? opts.axis : 'free',
    startX: evt.clientX, startY: evt.clientY
  };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onUp);
}

function began(evt) {
  var dx = Math.abs(evt.clientX - pending.startX);
  var dy = Math.abs(evt.clientY - pending.startY);
  if (pending.axis === 'free') return Math.max(dx, dy) >= THRESHOLD;
  return pending.axis === 'y'
    ? (dy >= THRESHOLD && dy > dx)
    : (dx >= THRESHOLD && dx > dy);
}

// The board scrolls in both axes under a pane and a top bar, so hit-test the
// point rather than computing where the columns ought to be.
function aim(evt) {
  var drag = store.ui.apptDrag;
  if (!drag) return;
  drag.over = null;

  var el = document.elementFromPoint(evt.clientX, evt.clientY);
  var cell = el && el.closest ? el.closest('.cell') : null;
  if (!cell) return;

  // The top strip of a square means "above this one", the way it does in any
  // list you can reorder. Deep enough to hit on a busy counter, shallow enough
  // that the middle of the square is still comfortably the square itself.
  var box = cell.getBoundingClientRect();
  var band = Math.min(EDGE_BAND, box.height * 0.34);
  var atEdge = evt.clientY - box.top <= band;

  /* Which side of a half square the pointer is on, measured against the slash
   * itself rather than against the little dashed box drawn in the corner. The
   * box is a third of a square wide and nobody lands a card inside a target
   * that size on a busy counter; the diagonal splits the whole square in two
   * and the far side is the whole of the bottom-right of it.
   *
   * It answers two questions now — which half is empty on a square still open,
   * and which of two turns a card landed on when both halves are taken. Same
   * measurement either way, because it is the same slash.
   *
   * Asked of every square, because only the store knows which ones are halves
   * — the same reason `atEdge` is measured here and judged there. */
  var atLowerHalf = box.width > 0 && box.height > 0 &&
    (evt.clientX - box.left) / box.width + (evt.clientY - box.top) / box.height > 1;

  drag.over = store.bookedDropAt(
    cell.getAttribute('data-staff-id'), Number(cell.getAttribute('data-row')),
    drag, atEdge, atLowerHalf
  );
}

function onMove(evt) {
  if (!pending) return;
  if (!dragging) {
    if (!began(evt)) return;
    dragging = true;
    store.ui.apptDrag = {
      link: pending.link, serviceId: pending.serviceId,
      staffIds: pending.staffIds,
      startsAt: pending.startsAt, durationMinutes: pending.durationMinutes,
      over: null
    };
    ghost.show = true;
    ghost.text = pending.label;
  }
  evt.preventDefault();
  ghost.x = evt.clientX;
  ghost.y = evt.clientY;
  aim(evt);
}

function onUp() {
  var drag = store.ui.apptDrag;
  var wasDragging = dragging;
  if (wasDragging && drag && drag.over) {
    var o = drag.over;
    if (o.kind === 'attach') store.attachToTurn(o.turnId, drag.link);
    // Work that is over, on a square that is taken. Two honest readings and the
    // board cannot pick — the sheet asks. The only drop here that puts nothing
    // on the board by itself.
    else if (o.kind === 'choose') store.openDropChoice(o.staffId, o.turnId, drag);
    else if (o.kind === 'insert') store.insertBookedAt(o.staffId, o.row, drag);
    // 'start' and 'pair' are the same call: a turn on the end of the column.
    // Completing a half square is not a special kind of booking, it is what
    // the end of that column happens to be — the square pairs itself.
    else store.startBookedOn(o.staffId, drag);
  }
  clear();
  // The pointerup that ends a drag also fires a click on the card it came
  // from; without this the card would open the assign sheet on landing.
  if (wasDragging) {
    suppress = true;
    setTimeout(function () { suppress = false; }, 0);
  }
}

export const apptDrag = {
  ghost: ghost,
  start: start,
  cancel: clear,
  suppressed: function () { return suppress; }
};

// Rendered once by the page rather than by each host, so a card dragged out
// of the pane and one dragged out of Due now carry the same label.
export const ApptGhost = {
  name: 'ApptGhost',
  setup: function () { return { ghost: ghost }; },
  template: [
    '<div v-if="ghost.show" class="appt-ghost"',
    '  :style="{ left: ghost.x + \'px\', top: ghost.y + \'px\' }">{{ ghost.text }}</div>'
  ].join('\n')
};
